import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AlaCarteContent, AlaCarteMenu } from '@core/interfaces/api';
import { ALACARTE_COLUMN_FLEX, MenuLogo, PAGE_BORDER } from '../shared/menu-print-shared';
import { EMPTY_CONTENT, cloneContent } from './alacarte-helpers';
import { ColumnEditor } from './alacarte-sortable';

// ─── New Menu Dialog ───────────────────────────────────────────────────────────

export function NewMenuDialog({
  open,
  onClose,
  onCreate,
  existingMenus,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (title: string, content: AlaCarteContent) => void;
  existingMenus: AlaCarteMenu[];
}) {
  const [title, setTitle] = useState('\u00c0 LA CARTE MENU');
  const [startMode, setStartMode] = useState<'blank' | 'copy'>('blank');
  const [copyFromId, setCopyFromId] = useState<number | ''>('');

  const handleCreate = () => {
    let content: AlaCarteContent = EMPTY_CONTENT;
    if (startMode === 'copy' && copyFromId !== '') {
      const source = existingMenus.find((m) => m.id === copyFromId);
      if (source) content = cloneContent(source.content);
    }
    onCreate(title, content);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New À La Carte Menu</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
          />
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>Start from</Typography>
            <RadioGroup value={startMode} onChange={(e) => setStartMode(e.target.value as 'blank' | 'copy')}>
              <FormControlLabel value="blank" control={<Radio size="small" />} label="Blank" />
              <FormControlLabel value="copy" control={<Radio size="small" />} label="Copy from existing" disabled={existingMenus.length === 0} />
            </RadioGroup>
            {startMode === 'copy' && existingMenus.length > 0 && (
              <Select
                value={copyFromId}
                onChange={(e) => setCopyFromId(e.target.value as number)}
                size="small"
                fullWidth
                displayEmpty
              >
                <MenuItem value="" disabled>Select a menu…</MenuItem>
                {existingMenus.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.title}</MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!title.trim() || (startMode === 'copy' && copyFromId === '')}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Save As Dialog ───────────────────────────────────────────────────────────

export function SaveAsDialog({
  open,
  onClose,
  onSave,
  defaultTitle,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (title: string) => void;
  defaultTitle: string;
}) {
  const [name, setName] = useState(defaultTitle);
  useEffect(() => { if (open) setName(defaultTitle); }, [open, defaultTitle]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Save As</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Menu name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={() => { onSave(name); onClose(); }} disabled={!name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── WYSIWYG Page ──────────────────────────────────────────────────────────────

const PAGE_STYLE: React.CSSProperties = {
  width: '8.5in',
  minHeight: '11in',
  boxSizing: 'border-box',
  backgroundColor: '#fff',
  fontFamily: '"Times New Roman", Times, serif',
  color: '#000',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
};

export function WysiwygPage({
  title,
  bugImageUrl,
  content,
  onTitleChange,
  onContentChange,
}: {
  title: string;
  bugImageUrl: string | null;
  content: AlaCarteContent;
  onTitleChange: (t: string) => void;
  onContentChange: (c: AlaCarteContent) => void;
}) {
  return (
    <div style={PAGE_STYLE}>
      <div style={{
        margin: '0.6in 0.7in',
        border: PAGE_BORDER,
        padding: '32px 44px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}>
        {/* Header: Logo, community name, editable title */}
        <div style={{ textAlign: 'center', marginBottom: '28px', flexShrink: 0 }}>
          {bugImageUrl && <MenuLogo bugImageUrl={bugImageUrl} />}
          <input
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Menu Title"
            style={{
              fontSize: '22px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              textAlign: 'center',
              border: '1px dashed #ccc',
              borderRadius: '4px',
              padding: '4px 8px',
              marginTop: '4px',
              width: '80%',
              background: 'transparent',
              outline: 'none',
            }}
          />
        </div>
        {/* Two columns */}
        <div style={{ display: 'flex', flex: 1 }}>
          {(() => {
            const layout = content.columnLayout ?? '1/3-2/3';
            const [leftFlex, rightFlex] = ALACARTE_COLUMN_FLEX[layout] ?? ALACARTE_COLUMN_FLEX['1/3-2/3'];
            return (
              <>
                <div style={{ flex: leftFlex, paddingRight: '14px' }}>
                  <ColumnEditor
                    sections={content.leftColumn}
                    isLeft={true}
                    onSectionsChange={(cols) => onContentChange({ ...content, leftColumn: cols })}
                  />
                </div>
                <div style={{ flex: rightFlex, paddingLeft: '14px' }}>
                  <ColumnEditor
                    sections={content.rightColumn}
                    isLeft={false}
                    onSectionsChange={(cols) => onContentChange({ ...content, rightColumn: cols })}
                  />
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
